import { isValidAbn, normaliseAbn } from "@stb/jurisdiction-au/abn"
import { z } from "zod"

/**
 * `customers`: the people and businesses an organisation invoices (T412, PRD R2). Not a sync
 * entity (ADR-012: invoicing is web only), so there are no revision or visibility columns.
 * Request schemas are strict; the stored/response schema is lenient (no `.max()`), because the
 * server parses every stored row with it.
 */

/** Largest payment term a customer may carry, in days. */
export const CUSTOMER_MAX_PAYMENT_TERMS_DAYS = 365

export const customerSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  name: z.string(),
  email: z.string().nullable().default(null),
  abn: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  payment_terms_days: z.number().int().nullable().default(null),
  notes: z.string().nullable().default(null),
  deleted_at: z.string().datetime({ offset: true }).nullable().default(null),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Customer = z.infer<typeof customerSchema>

/** ABN as typed by a person: spaces tolerated, stored without them. */
export const abnRequestSchema = z
  .string()
  .trim()
  .transform(normaliseAbn)
  .refine(isValidAbn, "abn must be a valid 11 digit ABN")

const optionalText = (max: number) => z.string().trim().max(max).nullable()

export const createCustomerRequestSchema = z.strictObject({
  id: z.uuid().optional(),
  name: z.string().trim().min(1).max(120),
  email: z.email().max(254).nullable().default(null),
  abn: abnRequestSchema.nullable().default(null),
  address: optionalText(300).default(null),
  payment_terms_days: z
    .number()
    .int()
    .min(0)
    .max(CUSTOMER_MAX_PAYMENT_TERMS_DAYS)
    .nullable()
    .default(null),
  notes: optionalText(1000).default(null),
})

export type CreateCustomerRequest = z.input<typeof createCustomerRequestSchema>
export type ParsedCreateCustomerRequest = z.infer<typeof createCustomerRequestSchema>

export const updateCustomerRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(120).optional(),
  email: z.email().max(254).nullable().optional(),
  abn: abnRequestSchema.nullable().optional(),
  address: optionalText(300).optional(),
  payment_terms_days: z
    .number()
    .int()
    .min(0)
    .max(CUSTOMER_MAX_PAYMENT_TERMS_DAYS)
    .nullable()
    .optional(),
  notes: optionalText(1000).optional(),
})

export type UpdateCustomerRequest = z.infer<typeof updateCustomerRequestSchema>
